import { useEffect, useState } from "react";
import Select from "react-select";
import ReactTooltip from "react-tooltip";

import { MoreToolsModal, useAutomatedModalState } from "./MoreToolsModal";
import { useWfhCountryInfoState } from "./useWfhCountryInfoState";
import { useAmplitudeEventLogger } from "./useAmplitudeEventLogger";

import "./App.css";

function App() {
  const { isLoading, countryInfoRecords, countryInfoOptions } =
    useWfhCountryInfoState();
  const { isModalOpen, onModalClose, triggerActionCounter } =
    useAutomatedModalState(3);
  const logCountryChangeEvent = useAmplitudeEventLogger(
    "remote_work_regulations_submit"
  );

  const [country, setCountry] = useState();

  useEffect(() => {
    if (country)
      logCountryChangeEvent({
        country: country?.country,
      });
  }, [country, logCountryChangeEvent]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const showData = triggerActionCounter((selectedValue) => {
    const matchRecord = countryInfoRecords.find(
      (country) => country.countryCode === selectedValue.value
    );

    setCountry(matchRecord);
  });

  return (
    <>
      <ReactTooltip />

      <div className="max-w-6xl pt-12 m-auto">
        <Select
          noOptionsMessage={() => (isLoading ? "Loading..." : "No options")}
          options={countryInfoOptions}
          onChange={showData}
          placeholder={<div>Please select a country</div>}
        />

        <MoreToolsModal isOpen={isModalOpen} onClose={onModalClose} />

        {country ? (
          <>
            <div className="p-4 mt-8 rounded">
              <div className="text-3xl font-bold">{country.country}</div>

              <div className="pb-4">
                <div>{country.rules}</div>
              </div>
              {country.taxNotes && (
                <>
                  <div className="mt-4 text-lg font-bold">Tax Information</div>
                  <div className="pb-4 border-b">
                    <div>{country.taxNotes}</div>
                  </div>
                </>
              )}

              <div className="mt-4 mb-2 text-2xl font-bold">Allowances</div>
              <div className="grid-cols-3 gap-4 pb-4 border-b md:grid">
                <div className="text-xl font-bold text-gray-800">
                  {country.allowance}
                </div>
                <div>
                  <div className="font-bold">
                    Recommended Allowances
                    <span
                      className="px-1 ml-2 bg-gray-300 rounded-full"
                      data-tip="Provided by most companies"
                    >
                      ?
                    </span>
                  </div>
                  {country.inclusiveAllowance?.length ? (
                    country.inclusiveAllowance.map((item) => (
                      <span
                        key={item}
                        className="inline-block px-1 py-1 mb-2 mr-2 text-xs font-bold text-white capitalize rounded bg-emerald-700"
                      >
                        {" "}
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm">Not specified</span>
                  )}
                </div>
                <div>
                  <div className="font-bold">
                    Additional Allowances
                    <span
                      className="px-1 ml-2 bg-gray-300 rounded-full"
                      data-tip="Provided by top companies"
                    >
                      ?
                    </span>
                  </div>
                  {country.additionalAllowance?.length ? (
                    country.additionalAllowance.map((item) => (
                      <span
                        key={item}
                        className="inline-block px-1 py-1 mr-2 text-xs font-bold text-white capitalize bg-purple-300 rounded"
                      >
                        {" "}
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm">Not specified</span>
                  )}
                </div>
              </div>
              <div className="mt-4 mb-2 text-2xl font-bold">Legal</div>
              <div className="text-xl">Data protection</div>
              <div>{country.GDPR}</div>
            </div>
            <div className="mt-2">
              Found this useful or see something wrong?{" "}
              <a
                target="_blank"
                className="font-bold text-blue-600 underline"
                rel="noreferrer"
                href="https://airtable.com/shrlstkzGs26UTz8K"
              >
                Share your feedback
              </a>
              <div className="mt-4 text-xs">
                Legal Disclaimer: The information provided in Oyster's remote
                work benefits guide contains only general information sourced
                from readily accessible and publicly available data* and our
                independent research. Oyster or its related entities, by means
                of this remote work benefits guide, do not render, nor intend to
                render, any professional advice or services. Oyster shares its
                knowledge with you based on its experience employing people
                around the globe. Please do not rely on this information to make
                any decision or take any action that may affect your finances or
                business and instead, you should consult a qualified
                professional adviser. Oyster does not make any representations,
                warranties or gives any undertakings (express or implied) as to
                the accuracy or completeness of this information, and none of
                Oyster, its employees or agents shall be liable or responsible
                for any loss or damage whatsoever arising directly or indirectly
                in connection with any person relying on it.
                <div className="mt-2">
                  References: Oyster routinely reviews and updates this
                  information based on, among others, the following sources:
                  Easily accessible{" "}
                  <a
                    target="_blank"
                    className="font-bold text-blue-600 underline"
                    rel="noreferrer"
                    href="https://assets.kpmg/content/dam/kpmg/ch/pdf/international-working-from-home-report-2021.pdf"
                  >
                    Third Party data
                  </a>{" "}
                  ; input from Oyster vendors and partners; publicly available
                  information such as government entity websites; and learnings
                  from Oyster's own global experience.
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="p-4 mt-8 font-bold text-center rounded ">
            Please select a country to see the remote work regulations.
            <img
              src="/oyster-empty-state.png"
              className="object-cover w-1/4 m-auto"
              alt="Oyster Remote Regulations"
            />
          </div>
        )}
      </div>
    </>
  );
}

export default App;
